import React, { useState } from "react";
import { Button } from "./ui/button"; // Shadcn button
// import { Input } from "./ui/input"; // Shadcn input
import { useNavigate } from "react-router-dom";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "./ui/input-otp";
import { useUserType } from "../context/UserTypeContext";
import { useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const Verify = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const [email, setEmail] = useState(""); // For tracking user email
  // const [otp, setOtp] = useState(""); // For tracking OTP input
  const [otpValues, setOtpValues] = useState(Array(6).fill(""));
  // const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [toastList, setToastList] = useState([]);
  const { setUserType } = useUserType();

  const navigate = useNavigate();

  const inputRefs = useRef([]);

  // Fetch email from localStorage (set during signup)
  React.useEffect(() => {
    const storedEmail = localStorage.getItem("signupEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  // Function to request OTP
  const handleRequestOTP = async () => {
    if (!email) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Email is not available. Please sign up again.",
          variant: "destructive",
        },
      ]);

      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/request-otp/${email}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description:
              responseData.data?.message || "OTP sent to your email!",
            variant: "default",
          },
        ]);
      } else {
        const responseData = await response.json();

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message ||
              "Failed to send OTP. Please try again.",
            variant: "destructive",
          },
        ]);
      }
    } catch (error) {
      console.error("Error requesting OTP:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
    }
  };

  // Function to verify OTP
  const handleVerifyOTP = async () => {
    const otp = otpValues.join(""); // Convert array to string

    if (!otp || otp.length < 6) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Please enter the OTP.",
          variant: "destructive",
        },
      ]);

      return;
    }

    setVerifyLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/user/verify/${email}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
        },
        body: JSON.stringify({ otp }),
      });

      const responseData = await response.json();

      if (response.ok) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description:
              responseData.data?.message || "OTP verified successfully!",
            variant: "default",
          },
        ]);

        setUserType("customer");

        navigate("/signin");
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message || "Invalid OTP. Please try again.",
            variant: "destructive",
          },
        ]);

        setVerifyLoading(false);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <main className="tw-h-screen tw-flex tw-flex-col tw-justify-center tw-items-center">
      <div className="tw-flex tw-flex-col tw-items-center tw-w-full tw-max-w-md tw-px-6 tw-space-y-6">
        <p className="tw-text-center tw-mb-6 tw-text-lg">
          Check the OTP sent to your email:&nbsp;
          <span className="tw-text-[#00B5ED] tw-font-medium">{email}</span> and
          enter it below to complete verification
        </p>
        <div className="tw-w-full">
          <label
            htmlFor="otp"
            className="tw-block tw-mb-2 tw-text-sm tw-font-medium"
          >
            Enter OTP
          </label>

          <div className="tw-w-full tw-flex tw-justify-center">
            <InputOTP maxLength={6}>
              <InputOTPGroup>
                {otpValues.map((_, index) => {
                  // ✅ Ensure a ref exists for each input
                  if (!inputRefs.current[index]) {
                    inputRefs.current[index] = React.createRef();
                  }

                  return (
                    <React.Fragment key={index}>
                      <InputOTPSlot
                        ref={inputRefs.current[index]} // ✅ Attach the ref
                        index={index}
                        values={otpValues}
                        handleChange={(i, value) => {
                          if (value.length <= 1) {
                            const newOtp = [...otpValues];
                            newOtp[i] = value;
                            setOtpValues(newOtp);

                            // ✅ Automatically focus the next field if current has a value
                            if (value && i < otpValues.length - 1) {
                              inputRefs.current[i + 1]?.current?.focus();
                            }
                          }
                        }}
                      />
                      {index === 2 && (
                        <InputOTPSeparator key={`sep-${index}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>
        <Button
          onClick={handleVerifyOTP}
          disabled={verifyLoading}
          className="tw-w-full "
        >
          {verifyLoading ? "VERIFYING..." : "VERIFY"}
        </Button>
      </div>

      <div className="tw-text-center tw-text-xs md:tw-text-sm tw-my-6">
        Didn't get OTP?{" "}
        <button
          onClick={handleRequestOTP}
          className="tw-text-[#00B5ED] tw-font-medium tw-bg-transparent hover:tw-underline hover:tw-bg-transparent"
        >
          Resend
        </button>
      </div>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </main>
  );
};

export default Verify;
